// client/src/components/configuracoes/listaEspera/ListaEsperaConfigPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Save, Info } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import { useListaEsperaConfig, useSalvarListaEsperaConfig } from '../../../hooks/useListaEspera';
import { useTemplatesMensagem } from '../../../hooks/useTemplateMensagem';

const ToggleSwitch = ({ checked, onChange, label }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
        <div
            onClick={() => onChange(!checked)}
            style={{
                width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', flexShrink: 0,
                background: checked ? 'var(--color-primary)' : '#d1d5db',
                transition: 'background 0.2s',
            }}
        >
            <div style={{
                position: 'absolute', top: 2, width: 20, height: 20, borderRadius: '50%',
                background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                left: checked ? 22 : 2,
                transition: 'left 0.2s',
            }} />
        </div>
        <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{label}</span>
    </label>
);

const ListaEsperaConfigPage = () => {
    const { data: config, isLoading, isError, error } = useListaEsperaConfig();
    const { data: templates = [], isLoading: templatesLoading } = useTemplatesMensagem();
    const salvarConfig = useSalvarListaEsperaConfig();

    const [ativo, setAtivo] = useState(false);
    const [templateMensagemId, setTemplateMensagemId] = useState('');

    useEffect(() => {
        if (config) {
            setAtivo(config.ativo ?? false);
            setTemplateMensagemId(config.templateMensagem?._id || '');
        }
    }, [config]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        salvarConfig.mutate({ ativo, templateMensagemId: templateMensagemId || null });
    }, [ativo, templateMensagemId, salvarConfig]);

    if (isLoading) return null;

    if (isError) {
        return (
            <div className="page-management-container">
                <PageHeader icon={<Clock />} title="Lista de Espera" subtitle="Configure a notificação automática de vaga" />
                <div className="table-empty-state">
                    <h3>Não foi possível carregar</h3>
                    <p>{error?.response?.data?.error || 'Erro ao carregar a configuração de lista de espera.'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-management-container">
            <PageHeader icon={<Clock />} title="Lista de Espera" subtitle="Configure a notificação automática de vaga" />
            <form onSubmit={handleSubmit}>
                <div className="content-panel">
                    <div className="form-section">
                        <h4 className="form-section-title">Notificação automática</h4>
                        <div className="form-group">
                            <ToggleSwitch
                                checked={ativo}
                                onChange={setAtivo}
                                label={ativo ? 'Notificação automática ativa' : 'Notificação automática desativada'}
                            />
                        </div>
                        <p className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            <Info size={14} /> Independente desta config, a equipe sempre vê na fila quando uma vaga compatível aparece — isto só liga o aviso automático pro cliente.
                        </p>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Template usado no aviso</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label">Mensagem de "vaga disponível"</label>
                                <select
                                    className="form-input"
                                    value={templateMensagemId}
                                    onChange={(e) => setTemplateMensagemId(e.target.value)}
                                    disabled={templatesLoading}
                                >
                                    <option value="">Selecione um template...</option>
                                    {templates.map((t) => (
                                        <option key={t._id} value={t._id}>{t.nome} ({t.canal})</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <p className="form-hint" style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                            Variáveis disponíveis no template: <code>{'{{cliente.primeiroNome}}'}</code>, <code>{'{{cliente.nomeCompleto}}'}</code>, <code>{'{{servico.nome}}'}</code>, <code>{'{{data}}'}</code>, <code>{'{{empresa.name}}'}</code>.
                            Crie ou edite templates em Templates → Mensagens.
                        </p>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="action-button action-button-primary" disabled={salvarConfig.isPending}>
                            <Save size={18} className="icon" /> {salvarConfig.isPending ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ListaEsperaConfigPage;
